export enum MessageType {
    Departure = "departure",
    Arrival = "arrival",
    Onblock = "onblock",
    Offblock = "offblock",
    Position = "position",
    Flightplan = "flightplan",
    Cancellation = "cancellation"
}

export enum FlightStatus {
    Scheduled = "scheduled",
    Filed = "filed",
    Active = "active",
    Completed = "completed",
    Cancelled = "cancelled"
}

export enum TimeType {
    Actual = "actual",
    Estimated = "estimated",
    Enroute = "enroute"
}

export interface MessageBase {
    messageType: MessageType;
    flightAwareFlightId: string;
    flightIdentifier: string;
    pitr: string;
}

export interface Waypoint {
    latitude: number;
    longitude: number;
    time?: string;
    name?: string;
    altitude?: number;
    groundSpeed?: number;
}

interface ReportedByFacility {
    facilityHash?: string;
    facilityName?: string;
}

interface HasIdentifiers {
    atcIdentifier?: string;
    aircraftRegistration?: string;
}

interface HasEstimatedTimes {
    estimatedDepartureTime?: string;
    estimatedArrivalTime?: string;
    enRouteTime?: string;
}

interface HasPosition {
    latitude?: number;
    longitude?: number;
}

interface HasOriginDestination {
    origin?: string;
    destination?: string;
}

export interface FlightPlan extends MessageBase, ReportedByFacility, HasEstimatedTimes, HasIdentifiers, HasPosition, HasOriginDestination {
    messageType: MessageType.Flightplan;
    filedDepartureTime: string;
    flightStatus: FlightStatus;
    fourDWaypoints?: Waypoint[];
    aircraftType?: string;
    cruisingAltitude?: number;
    predictedETA?: string;
    predictedETAMethod?: string;
    groundSpeed?: number;
    transponderModeSCode?: string;
    prefix?: string;
    suffix?: string;
    route?: string;
    filedCruisingSpeed?: number;
    trueCancel?: boolean;
    waypoints?: Waypoint[];
}

export interface Departure extends MessageBase, ReportedByFacility, HasEstimatedTimes, HasIdentifiers, HasOriginDestination {
    messageType: MessageType.Departure;
    departureTime: string;
    origin: string;
    arrivalTime?: string;
    aircraftType?: string;
    isSynthetic?: boolean;
    departureTimeType: TimeType;
}

export interface Arrival extends MessageBase, ReportedByFacility, HasEstimatedTimes, HasIdentifiers, HasOriginDestination {
    messageType: MessageType.Arrival;
    arrivalTime: string;
    arrivalTimeType: TimeType;
    isSynthetic?: boolean;
}

export interface Cancellation extends MessageBase, ReportedByFacility, HasEstimatedTimes, HasIdentifiers, HasOriginDestination {
    messageType: MessageType.Cancellation;
    origin: string;
    aircraftType?: string;
    filedCruisingAltitude?: number;
    filedDepartureTime?: string;
    groundSpeed?: number;
    transponderModeSCode?: string;
    route?: string;
    filedCruisingSpeed?: number;
    flightStatus?: FlightStatus;
    trueCancel?: boolean;
    waypoints?: Waypoint[]
}

export enum AirGround {
    Air = "air",
    Ground = "ground"
}

export enum UpdateType {
    AdsB = "adsB",
    Radar = "radar",
    Transoceanic = "transoceanic",
    Estimated = "estimated",
    Datalink = "datalink",
    Multilateration = "multilateration",
    AsdeX = "asdeX",
    SpaceAdsB = "spaceAdsB"
}

export enum AltitudeChange {
    Climbing = "climbing",
    Descending = "descending"
}

export interface Position extends MessageBase, HasIdentifiers, HasEstimatedTimes, HasOriginDestination {
    messageType: MessageType.Position;
    airGround: AirGround;
    reportTime: string;
    facilityHash: string;
    facilityName: string;
    latitude: number;
    longitude: number;
    updateType: UpdateType;
    aircraftType?: string;
    altitude?: number;
    altitudeChange?: AltitudeChange
    barometricAltitude?: number;
    fuelOnBoard?: number;
    gpsAltitude?: number;
    groundspeed?: number;
    heading?: number;
    transponderModeSCode?: string;
    outsideAirTemperature?: number;
    nextExpectedReportAltitude?: number;
    nextExpectedReportTime?: string;
    nextExpectedReportLatitude?: number;
    nextExpectedReportLongitude?: number;
    filedCruisingSpeed?: number;
    transponderSquawkCode?: string;
    waypoints?: Waypoint[];
    winds?: string;
}

export interface OffBlock extends MessageBase, HasOriginDestination, ReportedByFacility {
    messageType: MessageType.Offblock;
    reportTime: string;
}

export interface OnBlock extends MessageBase, HasOriginDestination, ReportedByFacility {
    messageType: MessageType.Onblock;
    reportTime: string;
}

export type FlightMessage = Departure | Arrival | OnBlock | OffBlock | Position | FlightPlan | Cancellation;